import { useState, useEffect } from 'react';
import { Switch } from './ui/switch';
import { Bell, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PushNotificationToggle({ className = '' }) {
  const { user, getAuthHeader } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);

  const [needsResubscribe, setNeedsResubscribe] = useState(false);

  useEffect(() => {
    const checkSupport = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) {
        setIsSupported(true);
        try {
          // Ensure service worker is registered
          let registration = await navigator.serviceWorker.getRegistration();
          if (!registration) {
            registration = await navigator.serviceWorker.register('/sw.js');
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          if (!registration) return;

          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);

          // SELF-HEALING — fixes the "I only get emails now" issue
          // Two failure modes after a PWA reinstall / SW update:
          //   1. Browser still has a local sub, but backend lost it → silently re-POST
          //   2. Browser has NO local sub, but Notification.permission === 'granted' → show banner
          if (!user) return;

          if (subscription) {
            // Mode 1: ensure the backend knows about this endpoint
            try {
              const subJson = subscription.toJSON();
              await axios.post(
                `${API}/push/subscribe`,
                { endpoint: subJson.endpoint, keys: subJson.keys },
                { headers: getAuthHeader() }
              );
            } catch (e) {
              console.warn('[Push] Silent re-sync failed (non-critical):', e?.message);
            }
          } else if (Notification.permission === 'granted') {
            // Mode 2: permission still granted but subscription evaporated
            // (PWA reinstall, SW update on some devices, OS housekeeping)
            setNeedsResubscribe(true);
          }
        } catch (error) {
          console.error('Error checking push subscription:', error);
        }
      }
    };
    checkSupport();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const handleToggle = async (enabled) => {
    if (enabled) {
      await subscribe();
    } else {
      await unsubscribe();
    }
  };

  const subscribe = async () => {
    if (!user) {
      toast.error('Please sign in to enable notifications');
      return;
    }

    setIsLoading(true);
    try {
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notifications blocked. Please allow them in your browser settings for this site.');
        return;
      }

      // Ensure service worker is registered and active
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js');
      }
      // Wait for it to be active
      if (registration.installing || registration.waiting) {
        await new Promise((resolve) => {
          const sw = registration.installing || registration.waiting;
          sw.addEventListener('statechange', () => {
            if (sw.state === 'activated') resolve();
          });
          // Timeout fallback
          setTimeout(resolve, 3000);
        });
      }

      // Get VAPID key
      const { data: vapidData } = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidData.vapid_public_key || vapidData.publicKey;

      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send to backend (flat payload matching PushSubscription model)
      const subJson = subscription.toJSON();
      await axios.post(
        `${API}/push/subscribe`,
        { endpoint: subJson.endpoint, keys: subJson.keys },
        { headers: getAuthHeader() }
      );

      setIsSubscribed(true);
      toast.success('Notifications enabled!');

      // Test notification
      try {
        await axios.post(`${API}/push/test`, {}, { headers: getAuthHeader() });
      } catch (e) {
        console.warn('Test push after subscribe failed (non-critical):', e?.message);
      }
    } catch (error) {
      console.error('Push subscribe error:', error);
      const errName = error?.name || '';
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (errName === 'NotAllowedError') {
        toast.error('Notifications are blocked. Go to your browser settings → Site Settings → Notifications → Allow for this site.');
      } else if (errName === 'NotSupportedError') {
        toast.error('This browser does not support web push notifications. Try Chrome, Edge, or Firefox.');
      } else if (status === 422) {
        toast.error(`Subscription rejected by server: ${detail || 'invalid payload'}`);
      } else if (status) {
        toast.error(`Server error (${status}): ${detail || 'try again'}`);
      } else if (error?.message?.includes('applicationServerKey')) {
        toast.error('VAPID key issue — please contact support.');
      } else {
        toast.error(`Could not enable notifications: ${error?.message || 'unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribe = async () => {
    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }
      await axios.delete(`${API}/push/unsubscribe`, { headers: getAuthHeader() }).catch(() => {});
      setIsSubscribed(false);
      toast.success('Notifications disabled');
    } catch (error) {
      console.error('Push unsubscribe error:', error);
      toast.error('Failed to disable notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const sendTestNotification = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/push/test`, {}, { headers: getAuthHeader() });
      toast.success('Test notification sent! Check your device.');
    } catch (error) {
      console.error('Test notification error:', error);
      const status = error?.response?.status;
      if (status === 404) {
        // Subscription missing from backend — try to resubscribe automatically
        toast.info('Reconnecting notifications...');
        try {
          await subscribe();
          // Retry test after resubscribing
          await axios.post(`${API}/push/test`, {}, { headers: getAuthHeader() });
          toast.success('Reconnected! Test notification sent.');
        } catch (e) {
          toast.error('Could not reconnect notifications. Try toggling off and on.');
        }
      } else {
        toast.error('Failed to send test notification');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const runDiagnostics = async () => {
    setIsLoading(true);
    setDiagnostics(null);
    try {
      const { data } = await axios.get(`${API}/push/diagnostics`, { headers: getAuthHeader() });
      setDiagnostics(data);
      const delivered = (data.live_test_results || []).filter(r => r.status?.startsWith('delivered')).length;
      if (delivered > 0) {
        toast.success(`Diagnostic complete: ${delivered} push${delivered !== 1 ? 'es' : ''} delivered`);
      } else if (data.subscriptions_count === 0) {
        toast.warning('No push subscriptions found. Toggle off and on to fix.');
      } else {
        toast.error('Push subscriptions exist but none delivered. See details below.');
      }
    } catch (e) {
      console.error('Diagnostic error:', e);
      toast.error('Diagnostic failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isSupported) {
    return (
      <div className={`flex items-center justify-between ${className}`}>
        <div className="flex items-center gap-3">
          <Bell className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Notifications not supported on this browser</span>
        </div>
      </div>
    );
  }

  return (
    <div className={className} data-testid="push-notification-toggle">
      {/* Self-heal banner: shown when permission is granted but subscription was lost
          (typically after PWA reinstall or SW update on some Android devices). */}
      {needsResubscribe && !isSubscribed && (
        <div
          className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3"
          data-testid="resubscribe-banner"
        >
          <Bell className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-xs">
            <p className="font-semibold text-amber-900 mb-0.5">Push notifications need to be re-enabled</p>
            <p className="text-amber-800 leading-relaxed">
              Your push subscription was lost (often happens after a PWA reinstall). Tap below to fix —
              you'll keep getting reminders by email until then.
            </p>
            <button
              onClick={async () => {
                await subscribe();
                if (Notification.permission === 'granted') setNeedsResubscribe(false);
              }}
              disabled={isLoading}
              className="mt-2 text-xs font-semibold text-amber-900 underline hover:text-amber-700 disabled:opacity-50"
              data-testid="resubscribe-btn"
            >
              {isLoading ? 'Re-enabling…' : 'Re-enable notifications'}
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
          ) : (
            <Bell className={`w-4 h-4 ${isSubscribed ? 'text-primary' : 'text-muted-foreground'}`} />
          )}
          <div>
            <p className="text-sm font-medium">{isSubscribed ? 'Notifications On' : 'Notifications Off'}</p>
            <p className="text-xs text-muted-foreground">
              {isSubscribed ? 'You\'ll receive supplement reminders' : 'Enable to get daily reminders'}
            </p>
          </div>
        </div>
        <Switch
          checked={isSubscribed}
          onCheckedChange={handleToggle}
          disabled={isLoading}
        />
      </div>
      {isSubscribed && (
        <div className="mt-3 space-y-2">
          <button
            onClick={sendTestNotification}
            disabled={isLoading}
            data-testid="send-test-notification-btn"
            className="w-full text-xs font-medium text-primary border border-primary/30 rounded-lg py-2 px-3 hover:bg-primary/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send test notification
          </button>
          <button
            onClick={runDiagnostics}
            disabled={isLoading}
            data-testid="run-diagnostics-btn"
            className="w-full text-xs font-medium text-muted-foreground border border-border rounded-lg py-2 px-3 hover:bg-muted/30 transition-colors disabled:opacity-50"
          >
            Run notification diagnostic
          </button>
          {diagnostics && (
            <div className="mt-2 p-3 bg-muted/20 rounded-lg text-[11px] space-y-1.5" data-testid="diagnostics-result">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Timezone</span>
                <span className="font-medium">{diagnostics.user_timezone}</span>
              </div>
              {diagnostics.scheduler_heartbeat && (
                <div className="flex justify-between" data-testid="scheduler-heartbeat">
                  <span className="text-muted-foreground">Scheduler</span>
                  <span className={`font-medium ${diagnostics.scheduler_heartbeat.status === 'healthy' ? 'text-green-700' : 'text-red-700'}`}>
                    {diagnostics.scheduler_heartbeat.status} · {diagnostics.scheduler_heartbeat.seconds_ago}s ago
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Active reminders</span>
                <span className="font-medium">{diagnostics.active_reminders}</span>
              </div>
              {diagnostics.reminders_detail?.length > 0 && (
                <div className="pl-2 space-y-0.5 text-[10px]" data-testid="reminders-detail">
                  {diagnostics.reminders_detail.map((r, i) => {
                    const nextFire = r.next_fire_at_local
                      ? new Date(r.next_fire_at_local).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })
                      : null;
                    return (
                      <div key={i} className={!r.enabled || r.days_count < 7 ? 'text-amber-700' : 'text-muted-foreground'}>
                        • {r.time} {r.supplement_name} — {r.days_label}{!r.enabled ? ' (disabled)' : ''}
                        {nextFire && <span className="text-[9px] opacity-70"> · next: {nextFire}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Push subscriptions</span>
                <span className="font-medium">{diagnostics.subscriptions_count}</span>
              </div>
              {diagnostics.subscriptions?.map((s, i) => (
                <div key={i} className="pl-2 text-muted-foreground">• {s.provider} ({s.has_keys ? 'keys ✓' : 'no keys ✗'})</div>
              ))}
              {diagnostics.live_test_results?.length > 0 && (
                <>
                  <div className="border-t border-border/40 pt-1.5 mt-1.5 font-medium">Live test:</div>
                  {diagnostics.live_test_results.map((r, i) => (
                    <div key={i} className={`pl-2 ${r.status.startsWith('delivered') ? 'text-green-700' : 'text-red-700'}`}>
                      • {r.status}
                    </div>
                  ))}
                </>
              )}
              {diagnostics.subscriptions_count === 0 && (
                <p className="text-amber-700 pt-1 font-medium">→ Toggle notifications OFF and ON to resubscribe</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
